import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizardStore } from '../../stores/wizardStore';
import {
  Building2,
  BarChart3,
  Users,
  FileText,
  CheckCircle2,
  AlertCircle,
  Pencil,
  ArrowRight,
  Sparkles,
  TrendingUp,
  DollarSign
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { clearDraft } from '../../lib/analysisWizardDraft';
import { toast } from 'sonner';
import { formatCurrencyValue, regionFromCountry } from '../../components/financial/utils/formatters';
import FocalCompanyFinancialPanel from '../../components/wizard/FocalCompanyFinancialPanel';
import EVAnalysisPanel from '../../components/case-analytics/EVAnalysisPanel';

interface Step5Props {
  onNext: () => void;
  onBack: () => void;
  caseId?: string; // If provided, we're in edit mode
}

export const Step5ReviewSubmit: React.FC<Step5Props> = ({ onNext, onBack, caseId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!caseId;
  const {
    companyData,
    industryData,
    competitorsData,
    questionsData,
    resetWizard
  } = useWizardStore();

  const [caseName, setCaseName] = useState(
    `${companyData?.name || 'Company'} - ${industryData?.name || 'Industry'} Analysis`
  );
  const [caseDescription, setCaseDescription] = useState(
    `Strategic analysis case for ${companyData?.name || 'company'} in the ${industryData?.classification || 'industry'} sector.`
  );
  const [submitting, setSubmitting] = useState(false);
  const [completeDiscovery, setCompleteDiscovery] = useState(false);

  // Get selected items
  const selectedCompetitors = competitorsData.filter(c => c.selected);
  const selectedQuestions = questionsData.filter(q => q.selected);

  // Validation
  const isValid = () => {
    return (
      caseName.trim().length > 0 &&
      caseDescription.trim().length > 0 &&
      companyData &&
      selectedCompetitors.length >= 2 &&
      selectedQuestions.length >= 3
    );
  };

  // Create or update case mutation
  const saveCaseMutation = useMutation({
    mutationFn: async ({ caseData, completeDiscovery }: { caseData: any, completeDiscovery: boolean }) => {
      // Step 1: Create/update the case
      const payload = {
        ...caseData,
        completeDiscovery
      };

      let caseResponse;
      if (isEditMode && caseId) {
        // Update existing case using PUT
        caseResponse = await api.put(`/cases/${caseId}`, payload);
      } else {
        // Create new case using POST
        caseResponse = await api.post('/cases', payload);
      }

      // Step 2: If completeDiscovery is true, trigger AI discovery
      if (completeDiscovery) {
        const createdCaseId = caseResponse.caseId;
        console.log('🚀 Triggering AI discovery for case:', createdCaseId);

        try {
          const discoveryResponse = await api.post(
            `/case-discovery/cases/${createdCaseId}/start`,
            { companyId: companyData?.companyId || `manual_${Date.now()}` },
            {
              headers: {
                'X-User-Email': 'user@example.com' // TODO: Get from auth context
              }
            }
          );

          console.log('✅ Discovery completed:', discoveryResponse);

          return {
            ...caseResponse,
            completeDiscovery,
            discoveryData: discoveryResponse.data
          };
        } catch (discoveryError) {
          console.error('❌ Discovery failed:', discoveryError);
          // Don't fail the whole operation if discovery fails
          // Case is already created, user can retry discovery later
          toast.warning('Case created but AI discovery failed. You can retry from the case details page.');
          return { ...caseResponse, completeDiscovery, discoveryError };
        }
      }

      return { ...caseResponse, completeDiscovery };
    },
    onSuccess: async (data: any) => {
      // Show success toast based on action
      let message;
      if (data.completeDiscovery) {
        if (data.discoveryData) {
          const { industry, competitors, personas } = data.discoveryData;
          message = `Case "${data.name}" created with AI-powered discovery! ` +
            `Generated: ${industry?.name || 'Industry research'}, ` +
            `${competitors?.count || 0} competitors, ` +
            `${personas?.count || 0} personas.`;
        } else if (data.discoveryError) {
          message = `Case "${data.name}" saved, but AI discovery failed. Please retry from case details.`;
        } else {
          message = `Case "${data.name}" saved! Discovery phase completed. Ready for Analysis phase.`;
        }
      } else {
        message = `Case "${data.name}" saved as draft. You can continue discovery later.`;
      }

      toast.success(message, { duration: 8000 });

      // Reset wizard
      resetWizard();

      // When Discovery was just completed the case transitions to the Analysis
      // phase.  Clear any stale per-case analysis draft so the FIRST time the
      // user opens the Analysis wizard it starts cleanly at Step 0 (Domain
      // Selection) instead of resuming a leftover draft (e.g. Data Mapping).
      // Cases the user genuinely works on later write their own draft and still
      // resume where they left off.
      if (data.completeDiscovery && data.caseId) {
        clearDraft(data.caseId, 'analysis');
      }

      // Invalidate and wait for cases cache to refresh before navigating
      // This ensures the Case Library shows updated data immediately
      await queryClient.invalidateQueries({ queryKey: ['cases'] });
      await queryClient.invalidateQueries({ queryKey: ['case', caseId] });

      // Navigate to the Case Library (where cases live) after cache is refreshed
      navigate('/case-library');
      // On "Complete Discovery" the backend advances the case to the Analysis
      // phase — take the user straight to the Analysis wizard landing (Step 0) for
      // this case, matching how the Case Library opens an Analysis-phase case. On
      // "Save Draft" the case stays in Discovery, so return to the dashboard.
      if (data.completeDiscovery && data.caseId) {
        navigate(`/cases/analysis-wizard?caseId=${data.caseId}&autoAnalyze=true`);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error) => {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} case:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} case. Please try again.`);
    }
  });

  const handleSubmit = async (shouldCompleteDiscovery: boolean) => {
    if (!isValid()) return;

    setSubmitting(true);
    setCompleteDiscovery(shouldCompleteDiscovery);

    // Prepare case data in backend API format
    const caseData = {
      name: caseName,
      description: caseDescription,
      companyId: companyData?.companyId || null,
      wizardData: {
        companyData,
        industryData,
        competitorsData: selectedCompetitors,
        questionsData: selectedQuestions,
        caseName,
        caseDescription
      }
    };

    console.log('📦 Saving case with wizard data:');
    console.log(`   Questions count: ${selectedQuestions.length}`);
    console.log('   Sample question:', selectedQuestions[0]);
    console.log('   Full questionsData:', selectedQuestions);

    try {
      await saveCaseMutation.mutateAsync({
        caseData,
        completeDiscovery: shouldCompleteDiscovery
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!companyData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
        <h3 className="text-xl font-semibold text-neutral-900 mb-2">Missing Data</h3>
        <p className="text-neutral-600 mb-4">
          Please complete all previous steps before reviewing and submitting.
        </p>
        <Button variant="outline" onClick={onBack}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
          <h2 className="text-3xl font-bold text-neutral-900">Review & Submit</h2>
        </div>
        <p className="text-neutral-600">
          Review your selections and create your Amplinth-powered case
        </p>
      </div>

      {/* Case Details */}
      <Card className="p-6 bg-gradient-to-br from-primary-50 to-white border-2 border-primary-200">
        <h3 className="text-xl font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary-600" />
          Case Details
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Case Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={caseName}
              onChange={(e) => setCaseName(e.target.value)}
              placeholder="Enter a name for this case"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              value={caseDescription}
              onChange={(e) => setCaseDescription(e.target.value)}
              placeholder="Describe the purpose and goals of this case"
              required
            />
          </div>
        </div>
      </Card>

      {/* Company Summary */}
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary-600" />
            <h3 className="text-xl font-semibold text-neutral-900">Company Selection</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="flex items-center gap-1 text-sm"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </Button>
        </div>

        <div className="space-y-3">
          <div>
            <h4 className="font-semibold text-lg text-neutral-900">
              {companyData.name}
              {companyData.ticker && (
                <span className="ml-2 text-primary-600">({companyData.ticker})</span>
              )}
            </h4>
            {companyData.industry && (
              <p className="text-sm text-neutral-600">{companyData.industry}</p>
            )}
          </div>

          {companyData.financialData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 p-4 bg-neutral-50 rounded-lg">
              {companyData.financialData.revenue && (
                <div className="flex items-start gap-2">
                  <DollarSign className="w-4 h-4 text-primary-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-neutral-600">Revenue</p>
                    <p className="font-semibold text-neutral-900 text-sm">
                      {companyData.financialData.revenue}
                    </p>
                  </div>
                </div>
              )}
              {companyData.financialData.revenueGrowth && (
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-neutral-600">Growth</p>
                    <p className="font-semibold text-neutral-900 text-sm">
                      {companyData.financialData.revenueGrowth}
                    </p>
                  </div>
                </div>
              )}
              {companyData.financialData.grossMargin && (
                <div>
                  <p className="text-xs text-neutral-600">Gross Margin</p>
                  <p className="font-semibold text-neutral-900 text-sm">
                    {companyData.financialData.grossMargin}
                  </p>
                </div>
              )}
              {companyData.financialData.operatingMargin && (
                <div>
                  <p className="text-xs text-neutral-600">Operating Margin</p>
                  <p className="font-semibold text-neutral-900 text-sm">
                    {companyData.financialData.operatingMargin}
                  </p>
                </div>
              )}
            </div>
          )}

          {companyData.financialData?.segments && companyData.financialData.segments.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-neutral-700 mb-2">Business Segments:</p>
              <div className="flex flex-wrap gap-2">
                {companyData.financialData.segments.map((segment, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-1 bg-white rounded-full text-sm border border-neutral-200"
                  >
                    {segment.name}
                    {segment.percentage && ` (${segment.percentage}%)`}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Latest financial snapshot from Company Research — surfaces real
              Revenue / Gross Margin / Operating Margin / ROE for the focal
              company in the same place the wizard's other steps show it. */}
          <FocalCompanyFinancialPanel
            companyKey={companyData.companyId || companyData.name || ''}
            companyId={companyData.companyId}
            companyName={companyData.name}
            country={companyData.country}
          />

          {/* Revenue → Enterprise Value Sankey — gives the user a final
              "value-creation story" view before submitting. Only mounts when
              the focal company has been processed through Company Research
              (the Sankey hook will short-circuit and render its own no-data
              state otherwise). */}
          {companyData.name && (
            <div className="mt-4">
              <EVAnalysisPanel
                companyName={companyData.name}
                country={companyData.country}
                mode="summary"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Industry Research Summary */}
      {industryData && (
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary-600" />
              <h3 className="text-xl font-semibold text-neutral-900">Industry Research</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-1 text-sm"
            >
              <Pencil className="w-3 h-3" />
              Edit
            </Button>
          </div>

          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-neutral-900">{industryData.name}</h4>
              <p className="text-sm text-neutral-600">{industryData.classification}</p>
            </div>

            {(industryData.marketSizeUSD || industryData.marketGrowthRate) && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-neutral-50 rounded-lg">
                {industryData.marketSizeUSD && (
                  <div>
                    <p className="text-xs text-neutral-600">Market Size</p>
                    <p className="font-semibold text-neutral-900">
                      {(() => {
                        const region = regionFromCountry(companyData?.country);
                        return region
                          ? formatCurrencyValue(industryData.marketSizeUSD, region, true)
                          : `$${(industryData.marketSizeUSD / 1e12).toFixed(1)}T`;
                      })()}
                    </p>
                  </div>
                )}
                {industryData.marketGrowthRate && (
                  <div>
                    <p className="text-xs text-neutral-600">Growth Rate</p>
                    <p className="font-semibold text-neutral-900">
                      {industryData.marketGrowthRate}%
                    </p>
                  </div>
                )}
              </div>
            )}

            {industryData.technologyTrends && industryData.technologyTrends.length > 0 && (
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-2">Key Trends:</p>
                <div className="flex flex-wrap gap-2">
                  {industryData.technologyTrends.slice(0, 5).map((trend, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                    >
                      {trend}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Competitors Summary */}
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-600" />
            <h3 className="text-xl font-semibold text-neutral-900">
              Selected Competitors ({selectedCompetitors.length})
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="flex items-center gap-1 text-sm"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </Button>
        </div>

        {selectedCompetitors.length < 2 ? (
          <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">
              Please select at least 2 competitors in Step 3
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedCompetitors.map((competitor) => (
              <Card
                key={competitor.competitorId}
                className="p-4 border border-neutral-200 bg-neutral-50"
              >
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <h4 className="font-semibold text-neutral-900">{competitor.name}</h4>
                    {competitor.market_position && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          competitor.market_position === 'Market Leader'
                            ? 'bg-green-100 text-green-700'
                            : competitor.market_position === 'Strong Challenger'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {competitor.market_position}
                      </span>
                    )}
                  </div>
                  {competitor.description && (
                    <p className="text-sm text-neutral-600 line-clamp-2">
                      {competitor.description}
                    </p>
                  )}
                  {competitor.market_share && (
                    <p className="text-xs text-neutral-500">
                      Market Share: {competitor.market_share}%
                    </p>
                  )}
                  {/* Compact financial snapshot — shows whichever metrics
                      Company Research has populated for this competitor.
                      Currency follows the case's country selection. */}
                  {competitor.financials && (
                    <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
                      {competitor.financials.revenue !== null && competitor.financials.revenue !== undefined && (
                        <div>
                          <span className="text-neutral-500">Revenue: </span>
                          <span className="font-medium text-neutral-900">
                            {(() => {
                              const region = regionFromCountry(companyData?.country);
                              return region
                                ? formatCurrencyValue(competitor.financials!.revenue, region, true)
                                : `$${((competitor.financials!.revenue ?? 0) / 1e9).toFixed(2)}B`;
                            })()}
                          </span>
                        </div>
                      )}
                      {competitor.financials.gross_margin !== null && competitor.financials.gross_margin !== undefined && (
                        <div>
                          <span className="text-neutral-500">Gross Margin: </span>
                          <span className="font-medium text-neutral-900">
                            {(Math.round((competitor.financials.gross_margin ?? 0) * 100) / 100).toFixed(2)}%
                          </span>
                        </div>
                      )}
                      {competitor.financials.operating_margin !== null && competitor.financials.operating_margin !== undefined && (
                        <div>
                          <span className="text-neutral-500">Operating Margin: </span>
                          <span className="font-medium text-neutral-900">
                            {(Math.round((competitor.financials.operating_margin ?? 0) * 100) / 100).toFixed(2)}%
                          </span>
                        </div>
                      )}
                      {competitor.financials.revenue_growth !== null && competitor.financials.revenue_growth !== undefined && (
                        <div>
                          <span className="text-neutral-500">Growth: </span>
                          <span className="font-medium text-neutral-900">
                            {competitor.financials.revenue_growth.toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Strategic Questions Summary */}
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary-600" />
            <h3 className="text-xl font-semibold text-neutral-900">
              Strategic Questions ({selectedQuestions.length})
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="flex items-center gap-1 text-sm"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </Button>
        </div>

        {selectedQuestions.length < 3 ? (
          <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">
              Please select at least 3 strategic questions in Step 4
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {selectedQuestions.slice(0, 6).map((question) => (
              <Card
                key={question.questionId}
                className="p-4 border border-neutral-200 bg-neutral-50"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <span className="text-xs font-medium text-primary-600">
                        {question.personaRole}
                      </span>
                      {question.priority && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            question.priority === 'Critical'
                              ? 'bg-red-100 text-red-700'
                              : question.priority === 'High'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {question.priority}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-900 font-medium">
                      {question.question}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
            {selectedQuestions.length > 6 && (
              <p className="text-sm text-neutral-500 text-center">
                +{selectedQuestions.length - 6} more questions
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Validation Warning */}
      {!isValid() && (
        <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Please complete all requirements:
            </p>
            <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
              {!caseName.trim() && <li>Enter a case name</li>}
              {!caseDescription.trim() && <li>Enter a case description</li>}
              {selectedCompetitors.length < 2 && <li>Select at least 2 competitors</li>}
              {selectedQuestions.length < 3 && <li>Select at least 3 strategic questions</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Navigation Buttons + inline help text. The help block is placed
          inside the right-side column (under the action buttons) so each
          description aligns under its respective button instead of floating
          off-center beneath the row. */}
      <div className="flex justify-between items-start pt-6 border-t border-neutral-200 gap-6">
        <Button variant="outline" onClick={onBack} disabled={submitting}>
          Back
        </Button>

        <div className="flex flex-col items-end gap-3">
          <div className="flex gap-3">
            {/* Save Draft Button */}
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={!isValid() || submitting}
              className="flex items-center gap-2"
            >
              {submitting && !completeDiscovery ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                  Saving Draft...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save Draft
                </>
              )}
            </Button>

            {/* Complete Discovery & Start Analysis Button */}
            <Button
              variant="primary"
              onClick={() => handleSubmit(true)}
              disabled={!isValid() || submitting}
              className="flex items-center gap-2"
            >
              {submitting && completeDiscovery ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Completing Discovery...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Complete Discovery & Start Analysis
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>

          {isValid() && !submitting && (
            <div className="text-sm text-neutral-500 space-y-2 self-stretch">
              <p className="font-medium text-neutral-600 text-right">Choose how to proceed:</p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="text-left">
                  <p className="font-semibold text-neutral-700">📝 Save Draft</p>
                  <p>Save progress and continue later</p>
                  <p>Discovery phase remains active</p>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-primary-600">✅ Complete Discovery</p>
                  <p>Mark discovery phase complete</p>
                  <p>Move case to Analysis phase</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step5ReviewSubmit;
